import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DollarSign,
  Plus,
  Trash2,
  ChevronLeft,
  TrendingUp,
  Calendar,
  AlertTriangle,
  CheckCircle,
  Download,
  RefreshCw,
  Receipt,
  Tv,
  Music,
  Dumbbell,
  Cloud,
  ShoppingBag,
  Gamepad2,
  BookOpen,
  Smartphone,
  MoreHorizontal,
  Save,
  X,
  Mail,
  Bell,
  Sparkles,
  Loader2,
  Info,
  Search,
  Edit3,
  ArrowRight,
  Target,
  PiggyBank,
  Zap,
} from "lucide-react";
import { toast } from "sonner";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SUBSCRIPTION_CATEGORIES = [
  { id: "streaming", label: "Streaming", icon: Tv, color: "bg-red-500" },
  { id: "music", label: "Music", icon: Music, color: "bg-green-500" },
  { id: "fitness", label: "Fitness", icon: Dumbbell, color: "bg-orange-500" },
  { id: "cloud", label: "Cloud", icon: Cloud, color: "bg-blue-500" },
  { id: "shopping", label: "Shopping", icon: ShoppingBag, color: "bg-purple-500" },
  { id: "gaming", label: "Gaming", icon: Gamepad2, color: "bg-indigo-500" },
  { id: "learning", label: "Learning", icon: BookOpen, color: "bg-emerald-500" },
  { id: "phone", label: "Phone", icon: Smartphone, color: "bg-slate-500" },
  { id: "other", label: "Other", icon: MoreHorizontal, color: "bg-gray-500" },
];

// Canadian subscription pricing (January 2026)
const PRESET_SUBSCRIPTIONS = [
  // Streaming
  { name: "Netflix", category: "streaming", plans: [
    { name: "Standard with Ads", price: 5.99 },
    { name: "Standard", price: 16.49 },
    { name: "Premium", price: 22.99 },
  ]},
  { name: "Disney+", category: "streaming", plans: [
    { name: "Standard with Ads", price: 7.99 },
    { name: "Standard", price: 11.99 },
    { name: "Premium", price: 14.99 },
  ]},
  { name: "Amazon Prime Video", category: "streaming", plans: [
    { name: "Monthly", price: 9.99 },
  ]},
  { name: "Crave", category: "streaming", plans: [
    { name: "Basic", price: 9.99 },
    { name: "Standard", price: 14.99 },
    { name: "Premium (HBO)", price: 22.99 },
  ]},
  { name: "YouTube Premium", category: "streaming", plans: [
    { name: "Individual", price: 13.99 },
    { name: "Family", price: 22.99 },
    { name: "Student", price: 8.49 },
  ]},
  { name: "Apple TV+", category: "streaming", plans: [
    { name: "Monthly", price: 12.99 },
  ]},
  { name: "Paramount+", category: "streaming", plans: [
    { name: "Essential", price: 6.99 },
    { name: "Standard", price: 11.99 },
  ]},
  
  // Music
  { name: "Spotify", category: "music", plans: [
    { name: "Individual", price: 11.99 },
    { name: "Duo", price: 16.99 },
    { name: "Family", price: 19.99 },
    { name: "Student", price: 5.99 },
  ]},
  { name: "Apple Music", category: "music", plans: [
    { name: "Individual", price: 11.99 },
    { name: "Family", price: 18.99 },
    { name: "Student", price: 5.99 },
  ]},
  { name: "Amazon Music", category: "music", plans: [
    { name: "Unlimited", price: 10.99 },
    { name: "Family", price: 17.99 },
  ]},
  { name: "Tidal", category: "music", plans: [
    { name: "HiFi", price: 11.99 },
    { name: "HiFi Plus", price: 23.99 },
  ]},
  
  // Cloud
  { name: "iCloud+", category: "cloud", plans: [
    { name: "50 GB", price: 1.29 },
    { name: "200 GB", price: 3.99 },
    { name: "2 TB", price: 12.99 },
  ]},
  { name: "Google One", category: "cloud", plans: [
    { name: "100 GB", price: 2.79 },
    { name: "200 GB", price: 3.99 },
    { name: "2 TB", price: 13.99 },
  ]},
  { name: "Dropbox", category: "cloud", plans: [
    { name: "Plus (2TB)", price: 13.99 },
    { name: "Professional", price: 24.99 },
  ]},
  { name: "Microsoft 365", category: "cloud", plans: [
    { name: "Personal", price: 9.99 },
    { name: "Family", price: 12.99 },
  ]},
  
  // Gaming
  { name: "Xbox Game Pass", category: "gaming", plans: [
    { name: "Core", price: 11.99 },
    { name: "Standard", price: 14.99 },
    { name: "Ultimate", price: 22.99 },
  ]},
  { name: "PlayStation Plus", category: "gaming", plans: [
    { name: "Essential", price: 11.99 },
    { name: "Extra", price: 18.99 },
    { name: "Premium", price: 22.99 },
  ]},
  { name: "Nintendo Online", category: "gaming", plans: [
    { name: "Individual", price: 4.99 },
  ]},
  { name: "EA Play", category: "gaming", plans: [
    { name: "Monthly", price: 6.99 },
  ]},
  
  // Fitness
  { name: "GoodLife Fitness", category: "fitness", plans: [
    { name: "Basic", price: 29.99 },
    { name: "Premium", price: 54.99 },
  ]},
  { name: "Fit4Less", category: "fitness", plans: [
    { name: "Black Card", price: 14.99 },
  ]},
  { name: "Planet Fitness", category: "fitness", plans: [
    { name: "Classic", price: 15.00 },
    { name: "Black Card", price: 27.99 },
  ]},
  { name: "Peloton", category: "fitness", plans: [
    { name: "App Only", price: 16.99 },
    { name: "All-Access", price: 55.00 },
  ]},
  
  // Learning
  { name: "ChatGPT Plus", category: "learning", plans: [
    { name: "Plus", price: 27.00 },
    { name: "Pro", price: 270.00 },
  ]},
  { name: "Claude Pro", category: "learning", plans: [
    { name: "Pro", price: 27.00 },
  ]},
  { name: "Skillshare", category: "learning", plans: [
    { name: "Annual", price: 13.99 },
  ]},
  { name: "MasterClass", category: "learning", plans: [
    { name: "Individual", price: 16.67 },
    { name: "Duo", price: 20.00 },
    { name: "Family", price: 23.33 },
  ]},
  { name: "Duolingo Plus", category: "learning", plans: [
    { name: "Super", price: 9.99 },
  ]},
  
  // Shopping
  { name: "Amazon Prime", category: "shopping", plans: [
    { name: "Monthly", price: 9.99 },
  ]},
  { name: "Costco", category: "shopping", plans: [
    { name: "Gold Star (Yearly)", price: 5.42 },
    { name: "Executive (Yearly)", price: 10.83 },
  ]},
  
  // Phone
  { name: "Bell", category: "phone", plans: [
    { name: "25GB", price: 55.00 },
    { name: "50GB", price: 70.00 },
    { name: "100GB", price: 90.00 },
  ]},
  { name: "Rogers", category: "phone", plans: [
    { name: "20GB", price: 55.00 },
    { name: "50GB", price: 70.00 },
    { name: "100GB", price: 90.00 },
  ]},
  { name: "Telus", category: "phone", plans: [
    { name: "25GB", price: 55.00 },
    { name: "50GB", price: 70.00 },
    { name: "Unlimited", price: 95.00 },
  ]},
  { name: "Freedom Mobile", category: "phone", plans: [
    { name: "15GB", price: 35.00 },
    { name: "30GB", price: 45.00 },
    { name: "50GB", price: 50.00 },
  ]},
  { name: "Koodo", category: "phone", plans: [
    { name: "10GB", price: 45.00 },
    { name: "25GB", price: 55.00 },
    { name: "50GB", price: 65.00 },
  ]},
  { name: "Fido", category: "phone", plans: [
    { name: "10GB", price: 40.00 },
    { name: "25GB", price: 50.00 },
    { name: "50GB", price: 65.00 },
  ]},
];

const SubscriptionTracker = ({ onBack }) => {
  const [subscriptions, setSubscriptions] = useState([]);
  const [monthlyBudget, setMonthlyBudget] = useState("");
  const [userEmail, setUserEmail] = useState("");
  const [userName, setUserName] = useState("");
  const [emailReminders, setEmailReminders] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [aiInsights, setAiInsights] = useState(null);
  const [showBrowseModal, setShowBrowseModal] = useState(false);
  const [selectedPreset, setSelectedPreset] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [customPrice, setCustomPrice] = useState("");
  const [billingDate, setBillingDate] = useState("");
  const [showAddStep, setShowAddStep] = useState("browse"); // browse | plan | date
  const [showCustomForm, setShowCustomForm] = useState(false);
  
  // Custom subscription form
  const [customSub, setCustomSub] = useState({
    name: "",
    category: "",
    price: "",
    nextBillingDate: "",
  });

  // Load from localStorage
  useEffect(() => {
    const saved = localStorage.getItem("compoundr_subscriptions");
    if (saved) setSubscriptions(JSON.parse(saved));
    const savedBudget = localStorage.getItem("compoundr_sub_budget");
    if (savedBudget) setMonthlyBudget(savedBudget);
    const savedEmail = localStorage.getItem("compoundr_sub_email");
    if (savedEmail) setUserEmail(savedEmail);
    const savedName = localStorage.getItem("compoundr_sub_username");
    if (savedName) setUserName(savedName);
    const savedReminders = localStorage.getItem("compoundr_sub_reminders");
    if (savedReminders) setEmailReminders(savedReminders === "true");
  }, []);

  // Save to localStorage
  useEffect(() => {
    localStorage.setItem("compoundr_subscriptions", JSON.stringify(subscriptions));
  }, [subscriptions]);

  useEffect(() => {
    if (monthlyBudget) localStorage.setItem("compoundr_sub_budget", monthlyBudget);
  }, [monthlyBudget]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-CA", {
      style: "currency",
      currency: "CAD",
      minimumFractionDigits: 2,
    }).format(amount);
  };

  const totalMonthly = subscriptions.reduce((sum, sub) => sum + (parseFloat(sub.price) || 0), 0);
  const totalYearly = totalMonthly * 12;

  const addSubscriptionWithDate = () => {
    if (!selectedPreset || !selectedPlan) return;
    
    const price = customPrice ? parseFloat(customPrice) : selectedPlan.price;
    
    if (!billingDate) {
      toast.error("Please select your next billing date - this is required for reminders!");
      return;
    }

    const now = new Date();
    const subscription = {
      id: `${now.getTime()}-${Math.random().toString(36).substr(2, 9)}`,
      name: selectedPreset.name,
      category: selectedPreset.category,
      planName: selectedPlan.name,
      price: price,
      nextBillingDate: billingDate,
      createdAt: now.toISOString(),
    };

    setSubscriptions([...subscriptions, subscription]);
    
    // Sync to backend if reminders enabled
    if (emailReminders && userEmail) {
      syncSubscriptionToBackend(subscription);
    }
    
    // Reset
    setShowBrowseModal(false);
    setSelectedPreset(null);
    setSelectedPlan(null);
    setCustomPrice("");
    setBillingDate("");
    setShowAddStep("browse");
    toast.success(`${selectedPreset.name} added with reminder for ${billingDate}!`);
  };

  const addCustomSubscription = () => {
    if (!customSub.name || !customSub.price || !customSub.category || !customSub.nextBillingDate) {
      toast.error("Please fill in all fields including billing date");
      return;
    }

    const now = new Date();
    const subscription = {
      id: `${now.getTime()}-${Math.random().toString(36).substr(2, 9)}`,
      name: customSub.name,
      category: customSub.category,
      planName: "Custom",
      price: parseFloat(customSub.price),
      nextBillingDate: customSub.nextBillingDate,
      createdAt: now.toISOString(),
    };

    setSubscriptions([...subscriptions, subscription]);
    
    if (emailReminders && userEmail) {
      syncSubscriptionToBackend(subscription);
    }
    
    setCustomSub({ name: "", category: "", price: "", nextBillingDate: "" });
    setShowCustomForm(false);
    toast.success(`${subscription.name} added!`);
  };

  const syncSubscriptionToBackend = async (subscription) => {
    try {
      await axios.post(`${API_URL}/api/subscriptions/sync`, {
        user_email: userEmail,
        user_name: userName || userEmail.split("@")[0],
        subscription: subscription,
      });
    } catch (error) {
      console.error("Sync error:", error);
    }
  };

  const removeSubscription = (id) => {
    setSubscriptions(subscriptions.filter((s) => s.id !== id));
    toast.success("Subscription removed");
  };

  const updateBillingDate = (id, newDate) => {
    setSubscriptions(subscriptions.map((s) => 
      s.id === id ? { ...s, nextBillingDate: newDate } : s
    ));
    toast.success("Billing date updated");
  };

  const getCategoryInfo = (categoryId) => {
    return SUBSCRIPTION_CATEGORIES.find((c) => c.id === categoryId) || SUBSCRIPTION_CATEGORIES[8];
  };

  const groupByCategory = () => {
    const grouped = {};
    subscriptions.forEach((sub) => {
      if (!grouped[sub.category]) grouped[sub.category] = [];
      grouped[sub.category].push(sub);
    });
    return grouped;
  };

  const getUpcomingBillings = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const fiveDaysFromNow = new Date(today.getTime() + 5 * 24 * 60 * 60 * 1000);
    
    return subscriptions
      .filter((sub) => {
        if (!sub.nextBillingDate) return false;
        const billingDate = new Date(sub.nextBillingDate);
        return billingDate >= today && billingDate <= fiveDaysFromNow;
      })
      .sort((a, b) => new Date(a.nextBillingDate) - new Date(b.nextBillingDate));
  };

  const handleEmailReminderToggle = async (enabled) => {
    if (enabled && !userEmail) {
      toast.error("Please enter your email first");
      return;
    }
    
    setEmailReminders(enabled);
    localStorage.setItem("compoundr_sub_reminders", enabled.toString());
    localStorage.setItem("compoundr_sub_email", userEmail);
    localStorage.setItem("compoundr_sub_username", userName);
    
    if (enabled) {
      try {
        await axios.post(`${API_URL}/api/subscriptions/enable-reminders`, {
          user_email: userEmail,
          user_name: userName || userEmail.split("@")[0],
          subscriptions: subscriptions,
        });
        toast.success("Reminders enabled! You'll get emails 5 days before each renewal.");
      } catch (error) {
        toast.error("Failed to enable reminders");
        setEmailReminders(false);
      }
    }
  };

  const analyzeSubscriptions = async () => {
    if (subscriptions.length < 2) {
      toast.error("Add at least 2 subscriptions for AI analysis");
      return;
    }
    
    setIsAnalyzing(true);
    try {
      const response = await axios.post(`${API_URL}/api/subscriptions/analyze`, {
        subscriptions: subscriptions,
        monthly_budget: monthlyBudget ? parseFloat(monthlyBudget) : null,
        total_monthly: totalMonthly,
      });
      setAiInsights(response.data);
    } catch (error) {
      toast.error("Analysis failed - try again");
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleDownloadPDF = async () => {
    const element = document.getElementById("subscription-summary");
    if (!element) return;
    try {
      const canvas = await html2canvas(element, { scale: 2 });
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const imgWidth = 190;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      pdf.addImage(imgData, "PNG", 10, 10, imgWidth, imgHeight);
      pdf.save("my-subscriptions.pdf");
      toast.success("PDF downloaded!");
    } catch (error) {
      toast.error("Download failed");
    }
  };

  const filteredPresets = PRESET_SUBSCRIPTIONS.filter(
    (p) => p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
           p.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const upcomingBillings = getUpcomingBillings();
  const grouped = groupByCategory();

  return (
    <div className="max-w-4xl mx-auto">
      <Button
        variant="ghost"
        onClick={() => window.location.href = '/tools'}
        className="mb-4 text-slate-600 hover:text-navy-700"
        data-testid="back-button"
      >
        <ChevronLeft className="w-4 h-4 mr-2" />
        Back to All Tools
      </Button>

      <div id="subscription-summary">
        {/* Header */}
        <Card className="border-0 shadow-xl mb-4 overflow-hidden">
          <div className="bg-gradient-to-r from-amber-500 to-orange-500 p-4 sm:p-6 text-white">
            <div className="flex items-center gap-3 mb-1">
              <Receipt className="w-7 h-7" />
              <h1 className="text-xl sm:text-2xl font-heading font-bold">Subscription Tracker</h1>
            </div>
            <p className="text-amber-100 text-sm">
              Track subscriptions, get AI insights, and receive billing reminders
            </p>
          </div>

          <CardContent className="p-4 sm:p-6">
            {/* Stats Row */}
            <div className="grid grid-cols-4 gap-2 sm:gap-4 mb-4">
              <div className="bg-slate-50 rounded-lg p-3 text-center">
                <p className="text-xs text-slate-500">Active</p>
                <p className="text-lg sm:text-xl font-bold text-navy-700">{subscriptions.length}</p>
              </div>
              <div className="bg-emerald-50 rounded-lg p-3 text-center">
                <p className="text-xs text-slate-500">Monthly</p>
                <p className="text-lg sm:text-xl font-bold text-emerald-600">{formatCurrency(totalMonthly)}</p>
              </div>
              <div className="bg-blue-50 rounded-lg p-3 text-center">
                <p className="text-xs text-slate-500">Yearly</p>
                <p className="text-lg sm:text-xl font-bold text-blue-600">{formatCurrency(totalYearly)}</p>
              </div>
              <div className="bg-amber-50 rounded-lg p-3 text-center">
                <p className="text-xs text-slate-500">Budget</p>
                <Input
                  type="number"
                  placeholder="$"
                  value={monthlyBudget}
                  onChange={(e) => setMonthlyBudget(e.target.value)}
                  className="text-center h-7 text-sm mt-1 p-1"
                />
              </div>
            </div>

            {/* Email Reminder Explanation */}
            <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3 mb-4">
              <div className="flex items-start gap-2">
                <Mail className="w-5 h-5 text-indigo-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium text-indigo-800 text-sm mb-1">Get Payment Reminders 5 Days Early</p>
                  <p className="text-xs text-indigo-700">
                    We&apos;ll email you before each subscription renews. If you&apos;re still using it, great - keep it! 
                    If not, you have time to cancel and save that money. No more surprise charges.
                  </p>
                </div>
              </div>
            </div>

            {/* Upcoming Billings Alert */}
            {upcomingBillings.length > 0 && (
              <div className="bg-amber-50 border-l-4 border-amber-400 rounded-r-lg p-3 mb-4">
                <div className="flex items-center gap-2 mb-1">
                  <Bell className="w-4 h-4 text-amber-600" />
                  <span className="font-medium text-amber-800 text-sm">Billing Soon (Next 5 Days)</span>
                </div>
                {upcomingBillings.map((sub) => (
                  <div key={sub.id} className="text-xs text-amber-700 flex justify-between">
                    <span>{sub.name}</span>
                    <span>{formatCurrency(sub.price)} on {new Date(sub.nextBillingDate).toLocaleDateString()}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Email Reminders - Compact */}
            <div className="bg-slate-50 rounded-lg p-3 mb-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-slate-600" />
                  <span className="font-medium text-slate-700 text-sm">Billing Reminders (5 days before)</span>
                </div>
                <Switch
                  checked={emailReminders}
                  onCheckedChange={handleEmailReminderToggle}
                  data-testid="email-reminder-toggle"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Input
                  placeholder="Your name"
                  value={userName}
                  onChange={(e) => setUserName(e.target.value)}
                  className="h-8 text-sm"
                />
                <Input
                  type="email"
                  placeholder="your@email.com"
                  value={userEmail}
                  onChange={(e) => setUserEmail(e.target.value)}
                  className="h-8 text-sm"
                />
              </div>
            </div>

            {/* Action Buttons */}
            <div className="grid grid-cols-3 gap-2">
              <Button
                onClick={() => setShowBrowseModal(true)}
                className="bg-amber-500 hover:bg-amber-600 text-sm h-10"
                data-testid="browse-btn"
              >
                <Plus className="w-4 h-4 mr-1" />
                Add Service
              </Button>
              <Button
                onClick={() => setShowCustomForm(true)}
                variant="outline"
                className="text-sm h-10"
                data-testid="custom-btn"
              >
                <Edit3 className="w-4 h-4 mr-1" />
                Custom
              </Button>
              <Button
                onClick={analyzeSubscriptions}
                disabled={isAnalyzing || subscriptions.length < 2}
                className="bg-indigo-500 hover:bg-indigo-600 text-sm h-10"
                data-testid="analyze-btn"
              >
                {isAnalyzing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1" />}
                AI Audit
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Pro Tips & Money-Saving Advice */}
        <Card className="border-0 shadow-lg mb-4 bg-gradient-to-br from-slate-50 to-slate-100">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-amber-600" />
              </div>
              <h3 className="font-heading font-bold text-navy-700">Pro Tips & Money-Saving Advice</h3>
            </div>
            
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="bg-white rounded-lg p-3 border-l-4 border-amber-400">
                <p className="font-medium text-slate-800 text-sm mb-1">⏱️ 5-Minute Subscription Audit</p>
                <p className="text-xs text-slate-600">
                  Open your credit card or bank statement right now. Look for recurring charges you don&apos;t recognize or forgot about. 
                  Most people find at least one subscription they can cancel!
                </p>
              </div>
              
              <div className="bg-white rounded-lg p-3 border-l-4 border-emerald-400">
                <p className="font-medium text-slate-800 text-sm mb-1">📅 Find Your Billing Dates</p>
                <p className="text-xs text-slate-600">
                  Check each subscription app or your bank statement to find exact billing dates. 
                  Add them here so you get reminders 5 days early. Enough time to cancel if needed.
                </p>
              </div>
              
              <div className="bg-white rounded-lg p-3 border-l-4 border-blue-400">
                <p className="font-medium text-slate-800 text-sm mb-1">📺 Consider Ad-Supported Plans</p>
                <p className="text-xs text-slate-600">
                  Netflix with Ads is $5.99 vs $22.99 Premium. That&apos;s $204/year saved! 
                  Disney+ with Ads saves $84/year. A few ads = more money to invest.
                </p>
              </div>
              
              <div className="bg-white rounded-lg p-3 border-l-4 border-purple-400">
                <p className="font-medium text-slate-800 text-sm mb-1">👥 Split Family Plans</p>
                <p className="text-xs text-slate-600">
                  Spotify Family ($19.99) split 6 ways = $3.33/person. YouTube Premium Family split = $3.83/person. 
                  Organize with roommates or family to share costs!
                </p>
              </div>
              
              <div className="bg-white rounded-lg p-3 border-l-4 border-pink-400">
                <p className="font-medium text-slate-800 text-sm mb-1">🔄 Rotate Streaming Services</p>
                <p className="text-xs text-slate-600">
                  Instead of paying for Netflix, Disney+, AND Crave monthly, subscribe to one at a time. 
                  Binge what you want, cancel, move to the next. Save 66% on streaming!
                </p>
              </div>
              
              <div className="bg-white rounded-lg p-3 border-l-4 border-indigo-400">
                <p className="font-medium text-slate-800 text-sm mb-1">🎓 Student Discounts</p>
                <p className="text-xs text-slate-600">
                  Spotify Student is $5.99 (save $72/year). Apple Music Student is $5.99. 
                  Amazon Prime Student is 50% off. Always check for student pricing first!
                </p>
              </div>
            </div>
            
            <div className="mt-3 bg-emerald-50 rounded-lg p-3">
              <p className="text-sm text-emerald-800">
                <strong>💡 Investment Math:</strong> Saving just $20/month on subscriptions = $240/year. 
                Invested for 10 years at 7% return, that grows to <strong>$3,360</strong>. 
                Small cuts today = big gains tomorrow!
              </p>
            </div>
          </CardContent>
        </Card>

        {/* AI Insights */}
        {aiInsights && (
          <Card className="border-0 shadow-xl mb-4 bg-gradient-to-br from-indigo-50 to-purple-50">
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-indigo-600" />
                  <h3 className="font-heading font-bold text-navy-700">AI Subscription Audit</h3>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setAiInsights(null)}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
              
              {/* Summary */}
              <div className="bg-white rounded-lg p-3 mb-3">
                <p className="text-slate-700 text-sm">{aiInsights.summary}</p>
              </div>

              {/* Recommendations */}
              {aiInsights.recommendations?.length > 0 && (
                <div className="space-y-2 mb-3">
                  <p className="text-xs font-semibold text-indigo-700 uppercase">Action Items</p>
                  {aiInsights.recommendations.map((rec, idx) => (
                    <div key={idx} className="bg-white rounded-lg p-3 border-l-4 border-indigo-400">
                      <div className="flex items-start gap-2">
                        <Target className="w-4 h-4 text-indigo-500 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="font-medium text-slate-800 text-sm">{rec.title}</p>
                          <p className="text-xs text-slate-600">{rec.description}</p>
                          {rec.potential_savings > 0 && (
                            <Badge className="bg-emerald-100 text-emerald-700 mt-1 text-xs">
                              Save {formatCurrency(rec.potential_savings)}/month
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Investment Potential */}
              {aiInsights.investment_potential && (
                <div className="bg-emerald-100 rounded-lg p-3">
                  <div className="flex items-center gap-2 mb-1">
                    <PiggyBank className="w-4 h-4 text-emerald-600" />
                    <span className="font-medium text-emerald-800 text-sm">Investment Potential</span>
                  </div>
                  <p className="text-xs text-emerald-700">{aiInsights.investment_potential}</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Subscriptions List */}
        {subscriptions.length > 0 ? (
          <Card className="border-0 shadow-xl mb-4">
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-heading text-navy-700">Your Subscriptions</CardTitle>
            </CardHeader>
            <CardContent className="p-4 pt-0">
              <div className="space-y-4">
                {Object.entries(grouped).map(([categoryId, subs]) => {
                  const catInfo = getCategoryInfo(categoryId);
                  const CategoryIcon = catInfo.icon;
                  const categoryTotal = subs.reduce((sum, s) => sum + parseFloat(s.price), 0);

                  return (
                    <div key={categoryId}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <div className={`w-6 h-6 ${catInfo.color} rounded-md flex items-center justify-center`}>
                            <CategoryIcon className="w-3 h-3 text-white" />
                          </div>
                          <span className="font-medium text-navy-700 text-sm">{catInfo.label}</span>
                          <Badge variant="outline" className="text-xs h-5">{subs.length}</Badge>
                        </div>
                        <span className="text-sm font-medium text-slate-600">{formatCurrency(categoryTotal)}/mo</span>
                      </div>

                      <div className="space-y-1.5 ml-8">
                        {subs.map((sub) => (
                          <div
                            key={sub.id}
                            className="flex items-center justify-between p-2 bg-slate-50 rounded-lg"
                            data-testid={`sub-${sub.id}`}
                          >
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-slate-800 text-sm truncate">
                                {sub.name}
                                {sub.planName && sub.planName !== "Custom" && (
                                  <span className="text-slate-400 font-normal ml-1">({sub.planName})</span>
                                )}
                              </p>
                              <div className="flex items-center gap-2 text-xs text-slate-500">
                                <span>{formatCurrency(sub.price)}/mo</span>
                                {sub.nextBillingDate && (
                                  <span className="flex items-center gap-1">
                                    <Calendar className="w-3 h-3" />
                                    {new Date(sub.nextBillingDate).toLocaleDateString()}
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-1">
                              {!sub.nextBillingDate && (
                                <Input
                                  type="date"
                                  className="h-7 w-32 text-xs"
                                  onChange={(e) => updateBillingDate(sub.id, e.target.value)}
                                  placeholder="Add date"
                                />
                              )}
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => removeSubscription(sub.id)}
                                className="h-7 w-7 p-0 text-red-500 hover:text-red-600 hover:bg-red-50"
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card className="border-0 shadow-xl mb-4">
            <CardContent className="p-8 text-center">
              <Receipt className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <h3 className="font-medium text-slate-600 mb-1">No subscriptions yet</h3>
              <p className="text-sm text-slate-500 mb-4">Add your subscriptions to track spending and get reminders</p>
              <Button onClick={() => setShowBrowseModal(true)} className="bg-amber-500 hover:bg-amber-600">
                <Plus className="w-4 h-4 mr-2" />
                Add Your First Subscription
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Quick Insight */}
        {subscriptions.length >= 2 && !aiInsights && (
          <Card className="border-0 shadow-xl mb-4 bg-gradient-to-br from-emerald-50 to-teal-50">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <Zap className="w-5 h-5 text-emerald-600 mt-0.5" />
                <div>
                  <p className="text-sm text-slate-700">
                    <strong>Quick math:</strong> Your {formatCurrency(totalMonthly)}/month = {formatCurrency(totalYearly)}/year. 
                    Invested at 7% for 10 years, that&apos;s <strong className="text-emerald-600">{formatCurrency(totalMonthly * 12 * 14)}</strong> potential growth!
                  </p>
                  <Button
                    onClick={analyzeSubscriptions}
                    variant="link"
                    className="p-0 h-auto text-indigo-600 text-sm"
                  >
                    Get personalized AI recommendations →
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Bottom Actions */}
      <div className="flex gap-2">
        <Button
          onClick={handleDownloadPDF}
          variant="outline"
          className="flex-1"
          disabled={subscriptions.length === 0}
        >
          <Download className="w-4 h-4 mr-2" />
          Download PDF
        </Button>
        <Button
          variant="outline"
          onClick={() => {
            setSubscriptions([]);
            setAiInsights(null);
            localStorage.removeItem("compoundr_subscriptions");
            toast.success("Cleared all subscriptions");
          }}
          disabled={subscriptions.length === 0}
        >
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>

      {/* Browse Modal */}
      <AnimatePresence>
        {showBrowseModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => {
              setShowBrowseModal(false);
              setSelectedPreset(null);
              setSelectedPlan(null);
              setShowAddStep("browse");
              setSearchTerm("");
              setCustomPrice("");
              setBillingDate("");
            }}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[85vh] overflow-hidden"
            >
              {/* Header */}
              <div className="p-4 border-b bg-slate-50">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-heading font-bold text-navy-700">
                    {showAddStep === "browse" && "Canadian Services"}
                    {showAddStep === "plan" && selectedPreset?.name}
                    {showAddStep === "date" && "Set Billing Date"}
                  </h2>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      if (showAddStep === "date") {
                        setShowAddStep("plan");
                      } else if (showAddStep === "plan") {
                        setSelectedPreset(null);
                        setShowAddStep("browse");
                      } else {
                        setShowBrowseModal(false);
                        setSearchTerm("");
                      }
                    }}
                  >
                    {showAddStep === "browse" ? <X className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
                  </Button>
                </div>
                
                {showAddStep === "browse" && (
                  <div className="relative mt-3">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      placeholder="Search services..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-9"
                      autoFocus
                    />
                  </div>
                )}
              </div>

              {/* Content */}
              <div className="p-4 overflow-y-auto max-h-[60vh]">
                {showAddStep === "browse" && (
                  <div className="space-y-4">
                    {SUBSCRIPTION_CATEGORIES.map((cat) => {
                      const categoryPresets = filteredPresets.filter((p) => p.category === cat.id);
                      if (categoryPresets.length === 0) return null;
                      const CategoryIcon = cat.icon;
                      
                      return (
                        <div key={cat.id}>
                          <div className="flex items-center gap-2 mb-2">
                            <div className={`w-5 h-5 ${cat.color} rounded flex items-center justify-center`}>
                              <CategoryIcon className="w-3 h-3 text-white" />
                            </div>
                            <span className="font-medium text-slate-700 text-sm">{cat.label}</span>
                          </div>
                          <div className="grid grid-cols-3 gap-2">
                            {categoryPresets.map((preset) => (
                              <button
                                key={preset.name}
                                onClick={() => {
                                  setSelectedPreset(preset);
                                  setShowAddStep("plan");
                                }}
                                className="p-2 bg-slate-50 hover:bg-amber-50 rounded-lg text-left transition-colors"
                              >
                                <p className="font-medium text-slate-800 text-sm truncate">{preset.name}</p>
                                <p className="text-xs text-slate-500">
                                  from {formatCurrency(Math.min(...preset.plans.map((p) => p.price)))}
                                </p>
                              </button>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {showAddStep === "plan" && selectedPreset && (
                  <div className="space-y-3">
                    <p className="text-sm text-slate-600">Select your plan for {selectedPreset.name}:</p>
                    {selectedPreset.plans.map((plan, idx) => (
                      <button
                        key={idx}
                        onClick={() => {
                          setSelectedPlan(plan);
                          setShowAddStep("date");
                        }}
                        className="w-full flex items-center justify-between p-3 bg-slate-50 hover:bg-emerald-50 rounded-lg transition-colors border border-transparent hover:border-emerald-300"
                      >
                        <div>
                          <p className="font-medium text-slate-800">{plan.name}</p>
                          <p className="text-sm text-slate-500">{formatCurrency(plan.price)}/month</p>
                        </div>
                        <ArrowRight className="w-4 h-4 text-slate-400" />
                      </button>
                    ))}
                    
                    {/* Custom Price Option */}
                    <div className="border-t pt-3 mt-3">
                      <p className="text-xs text-slate-500 mb-2">Different price? Enter your actual cost:</p>
                      <div className="flex gap-2">
                        <div className="relative flex-1">
                          <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                          <Input
                            type="number"
                            step="0.01"
                            placeholder="Your price"
                            value={customPrice}
                            onChange={(e) => setCustomPrice(e.target.value)}
                            className="pl-9"
                          />
                        </div>
                        <Button
                          onClick={() => {
                            if (!customPrice) {
                              toast.error("Enter your custom price");
                              return;
                            }
                            setSelectedPlan({ name: "Custom", price: parseFloat(customPrice) });
                            setShowAddStep("date");
                          }}
                          disabled={!customPrice}
                          className="bg-amber-500 hover:bg-amber-600"
                        >
                          Use This Price
                        </Button>
                      </div>
                    </div>
                  </div>
                )}

                {showAddStep === "date" && selectedPreset && selectedPlan && (
                  <div className="space-y-4">
                    <div className="bg-amber-50 rounded-lg p-4 text-center">
                      <p className="text-lg font-bold text-navy-700">{selectedPreset.name}</p>
                      <p className="text-slate-600">{selectedPlan.name} • {formatCurrency(customPrice || selectedPlan.price)}/month</p>
                    </div>

                    <div>
                      <Label className="text-sm font-medium mb-2 block">
                        <Calendar className="w-4 h-4 inline mr-1" />
                        When is your next billing date? <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        type="date"
                        value={billingDate}
                        onChange={(e) => setBillingDate(e.target.value)}
                        className="text-center"
                        data-testid="billing-date-input"
                      />
                      <p className="text-xs text-slate-500 mt-2">
                        <Info className="w-3 h-3 inline mr-1" />
                        Check your bank statement or the app to find your billing date. We&apos;ll remind you 5 days before!
                      </p>
                    </div>

                    <Button
                      onClick={addSubscriptionWithDate}
                      className="w-full bg-emerald-500 hover:bg-emerald-600"
                      disabled={!billingDate}
                    >
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Add {selectedPreset.name}
                    </Button>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Custom Form Modal */}
      <AnimatePresence>
        {showCustomForm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowCustomForm(false)}
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-xl shadow-2xl w-full max-w-md p-5"
            >
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-heading font-bold text-navy-700">Add Custom Subscription</h2>
                <Button variant="ghost" size="sm" onClick={() => setShowCustomForm(false)}>
                  <X className="w-4 h-4" />
                </Button>
              </div>

              <div className="space-y-4">
                <div>
                  <Label className="text-sm">Service Name *</Label>
                  <Input
                    placeholder="e.g., Gym membership"
                    value={customSub.name}
                    onChange={(e) => setCustomSub({ ...customSub, name: e.target.value })}
                  />
                </div>
                <div>
                  <Label className="text-sm">Category *</Label>
                  <Select
                    value={customSub.category}
                    onValueChange={(v) => setCustomSub({ ...customSub, category: v })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {SUBSCRIPTION_CATEGORIES.map((cat) => (
                        <SelectItem key={cat.id} value={cat.id}>{cat.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-sm">Monthly Price *</Label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="0.00"
                      value={customSub.price}
                      onChange={(e) => setCustomSub({ ...customSub, price: e.target.value })}
                      className="pl-9"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-sm">Next Billing Date *</Label>
                  <Input
                    type="date"
                    value={customSub.nextBillingDate}
                    onChange={(e) => setCustomSub({ ...customSub, nextBillingDate: e.target.value })}
                  />
                </div>

                <Button
                  onClick={addCustomSubscription}
                  className="w-full bg-emerald-500 hover:bg-emerald-600"
                >
                  <Save className="w-4 h-4 mr-2" />
                  Add Subscription
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SubscriptionTracker;
